import { z } from 'zod';

/**
 * `pending-privacy-compliance-scanner` v0 — wire types for the
 * GDPR/HIPAA/SOC2 scan layer that runs alongside any test_run with
 * a `scan_profile` set.
 *
 * Locked decisions (this sweep):
 *   - One profile per run; mutually exclusive enum.
 *   - Findings are advisory (don't fail the run) in v0. v1 adds an
 *     org-level "block on critical" toggle.
 *   - PII fingerprint is SHA-256 of the canonical offending text;
 *     raw value is NEVER persisted.
 */

export const SCAN_PROFILES = ['gdpr', 'hipaa', 'soc2'] as const;
export type ScanProfile = (typeof SCAN_PROFILES)[number];

export const SCAN_SEVERITIES = ['info', 'low', 'medium', 'high', 'critical'] as const;
export type ScanSeverity = (typeof SCAN_SEVERITIES)[number];

export const SCAN_FINDING_KINDS = [
  'pii_in_url',
  'pii_in_storage',
  'unencrypted_request',
  'missing_consent_banner',
  'cookie_without_secure_flag',
  'phi_in_screenshot',
  'audit_log_gap',
  'other',
] as const;
export type ScanFindingKind = (typeof SCAN_FINDING_KINDS)[number];

export const ScanFindingEvidenceSchema = z.object({
  /** URL or context the finding was observed at — never a raw PII value. */
  url: z.string().optional(),
  /** Free-form, redacted observation suitable for the UI. */
  note: z.string().optional(),
  /** Header / cookie / storage key name (the WHAT, not the value). */
  fieldName: z.string().optional(),
});
export type ScanFindingEvidence = z.infer<typeof ScanFindingEvidenceSchema>;

export const ScanFindingSchema = z.object({
  id: z.string().uuid(),
  orgId: z.string().uuid(),
  projectId: z.string().uuid(),
  runId: z.string().uuid(),
  scanProfile: z.enum(SCAN_PROFILES),
  ruleId: z.string(),
  severity: z.enum(SCAN_SEVERITIES),
  findingKind: z.enum(SCAN_FINDING_KINDS),
  /** SHA-256 of the canonical offending text; null if not applicable. */
  piiFingerprint: z.string().nullable(),
  stepIndex: z.number().int().nonnegative().nullable(),
  evidence: ScanFindingEvidenceSchema,
  createdAt: z.string().datetime(),
});
export type ScanFinding = z.infer<typeof ScanFindingSchema>;

export const ScanFindingCreateRequestSchema = z.object({
  runId: z.string().uuid(),
  projectId: z.string().uuid(),
  scanProfile: z.enum(SCAN_PROFILES),
  ruleId: z.string().min(1).max(100),
  severity: z.enum(SCAN_SEVERITIES),
  findingKind: z.enum(SCAN_FINDING_KINDS),
  piiFingerprint: z
    .string()
    .regex(/^[a-f0-9]{64}$/)
    .nullable()
    .default(null),
  stepIndex: z.number().int().nonnegative().nullable().default(null),
  evidence: ScanFindingEvidenceSchema.default({}),
});
export type ScanFindingCreateRequest = z.infer<typeof ScanFindingCreateRequestSchema>;

// ============================================================================
// v0.1 (Ship 53) — new wire shape aligned with the Saturday Ship 44 pure
// scanner library (`packages/api/src/lib/privacy/scanner.ts`).
//
// Coexists with the legacy `ScanFinding` shape above (which the
// pre-Ship-44 advisory-only services use). New v0.1 surfaces consume
// the `PrivacyFinding*` types below. Locked decisions are reflected:
//
//   §1 post-run hook (not in-run interception) — server-side enrichment.
//   §2 5 finding kinds frozen.
//   §3 4 profiles (gdpr / hipaa / soc2 / custom).
//   §5 heuristic matchers — no ML.
//   §8 dedup at (kind, ruleId, domain, urlPattern).
//   §9 NO raw PII in `payload` — enforced by `PrivacyFindingPayloadSchema`.
// ============================================================================

/** Finding kinds — frozen at v0.1. Adding a new kind is a contract bump. */
export const PRIVACY_FINDING_KINDS = [
  'pii_in_url',
  'pii_in_console',
  'pii_in_request_body',
  'cookie_set_by_third_party',
  'third_party_call',
] as const;
export type PrivacyFindingKind = (typeof PRIVACY_FINDING_KINDS)[number];

export const PRIVACY_SEVERITIES = ['high', 'med', 'low'] as const;
export type PrivacySeverity = (typeof PRIVACY_SEVERITIES)[number];

export const PRIVACY_SCAN_PROFILES = ['gdpr', 'hipaa', 'soc2', 'custom'] as const;
export type PrivacyScanProfile = (typeof PRIVACY_SCAN_PROFILES)[number];

/**
 * Sanitised payload — NEVER contains raw matched bytes (locked
 * decision §9). The library `redactMatch` writes only the rule id +
 * sanitised URL + short SHA-256 prefix.
 */
export const PrivacyFindingPayloadSchema = z.object({
  /** Matcher rule id (e.g. `email-address`, `aws-access-key`). */
  ruleId: z.string().min(1).max(100),
  /** Sanitised URL pattern (host + path + sorted query KEYS, no values). */
  urlPattern: z.string().optional(),
  /** Registered domain associated with the finding (third party / cookie origin). */
  domain: z.string().optional(),
  /** Set-Cookie attribute KEYS — never the cookie VALUE. */
  cookieAttrs: z.array(z.string()).optional(),
  /** 8-char SHA-256 prefix of the matched bytes — too short to reverse. */
  prefixHash: z
    .string()
    .regex(/^[a-f0-9]{8}$/)
    .optional(),
  /** Where the match came from. */
  source: z.enum(['url', 'console', 'body', 'set-cookie', 'network']),
});
export type PrivacyFindingPayload = z.infer<typeof PrivacyFindingPayloadSchema>;

/** Wire shape returned by `GET /api/runs/:runId/privacy-findings`. */
export const PrivacyFindingSchema = z.object({
  id: z.string().uuid(),
  runId: z.string().uuid(),
  orgId: z.string().uuid(),
  projectId: z.string().uuid(),
  kind: z.enum(PRIVACY_FINDING_KINDS),
  severity: z.enum(PRIVACY_SEVERITIES),
  ruleId: z.string(),
  urlPattern: z.string().nullable(),
  domain: z.string().nullable(),
  payload: PrivacyFindingPayloadSchema,
  createdAt: z.string().datetime(),
});
export type PrivacyFinding = z.infer<typeof PrivacyFindingSchema>;

/**
 * Custom matcher rule. Closes the v1 row alongside v0.1 — operators
 * paste their own regex (e.g. internal API key shapes) into
 * admin-config and the scanner picks them up on the next run. Limits:
 *
 *   - 100 max custom rules per org (defensive bound; the scanner is
 *     O(rules * text-length) per source).
 *   - `pattern` is validated as a real regex at admin-config write
 *     time; the runtime layer compiles with the global flag.
 *   - Names + ids must be `[a-z0-9-]{1,40}` so they're safe to log
 *     and stable across exports.
 */
export const PrivacyCustomMatcherSchema = z.object({
  id: z
    .string()
    .min(1)
    .max(40)
    .regex(/^[a-z0-9-]+$/),
  name: z.string().min(1).max(80),
  pattern: z.string().min(1).max(500),
  severity: z.enum(PRIVACY_SEVERITIES),
});
export type PrivacyCustomMatcher = z.infer<typeof PrivacyCustomMatcherSchema>;

/**
 * Runner-captured signals. Optional on `POST /api/runs`: when
 * `privacy.enabled === false` (or the runner is older than Ship 53)
 * the field is omitted and the scanner falls back to URLs we already
 * have on `step_results[].pageUrl`.
 *
 * Capture caps are enforced runner-side; the wire schema reflects
 * the post-capture shape so the API can refuse oversize payloads
 * without a per-field guard.
 */
export const PrivacySignalsSchema = z.object({
  /** Distinct page URLs the run visited (navigate + redirects). Max 50. */
  urls: z.array(z.string().max(2048)).max(50),
  /** Console messages captured during the run. Max 100. */
  consoleMessages: z
    .array(
      z.object({
        level: z.enum(['log', 'warn', 'error']),
        text: z.string().max(2048),
      }),
    )
    .max(100),
  /** Network requests captured during the run. Max 100. */
  networkRequests: z
    .array(
      z.object({
        url: z.string().max(2048),
        method: z.string().max(10),
        /** Optional first-2KB body preview (only when `captureBodies='snippet'`). */
        bodyPreview: z.string().max(2048).optional(),
        /** Set-Cookie response headers (one per array entry). */
        setCookieHeaders: z.array(z.string().max(2048)).max(20).optional(),
      }),
    )
    .max(100),
});
export type PrivacySignals = z.infer<typeof PrivacySignalsSchema>;

/** `GET /api/runs/:runId/privacy-findings` response. */
export const PrivacyFindingsForRunResponseSchema = z.object({
  findings: z.array(PrivacyFindingSchema),
});
export type PrivacyFindingsForRunResponse = z.infer<typeof PrivacyFindingsForRunResponseSchema>;

/**
 * Cookie-by-domain rollup row. The weekly report folds every
 * `cookie_set_by_third_party` finding into a `{ domain, names[] }`
 * tuple so the SPA can render a flat "third-party cookies set in
 * the last 7 days" table.
 */
export const PrivacyCookieByDomainSchema = z.object({
  domain: z.string(),
  cookieNames: z.array(z.string()),
});
export type PrivacyCookieByDomain = z.infer<typeof PrivacyCookieByDomainSchema>;

/** Weekly report — `GET /api/projects/:projectId/privacy/report?week=YYYY-WW`. */
export const WeeklyPrivacyReportSchema = z.object({
  projectId: z.string().uuid(),
  /** ISO week — `YYYY-Www` (e.g. `2026-W22`). Inclusive Mon-Sun window. */
  week: z.string().regex(/^\d{4}-W\d{2}$/),
  /** Inclusive start of the report window in UTC. */
  windowStart: z.string().datetime(),
  /** Exclusive end of the report window in UTC (= windowStart + 7d). */
  windowEnd: z.string().datetime(),
  /** Dedup-d findings — at most one row per (kind, ruleId, domain, urlPattern). */
  findings: z.array(PrivacyFindingSchema),
  /** Aggregate third-party registered domains the project talked to. */
  thirdPartyDomains: z.array(z.string()),
  /** Cookies set by third parties, grouped by registered domain. */
  cookiesByDomain: z.array(PrivacyCookieByDomainSchema),
  /** Total findings BEFORE dedup. UI uses this for the "X raw findings collapsed to Y" badge. */
  rawFindingCount: z.number().int().nonnegative(),
});
export type WeeklyPrivacyReport = z.infer<typeof WeeklyPrivacyReportSchema>;

/**
 * Public-config slice — what the SPA sees about admin privacy
 * config. Echoes ONLY the kill switch + active profile + custom-
 * matcher count (the matchers themselves are not exposed; they may
 * contain regexes that touch on org-internal naming conventions).
 */
export const PublicPrivacyConfigSchema = z.object({
  enabled: z.boolean(),
  scanProfile: z.enum(PRIVACY_SCAN_PROFILES),
  captureBodies: z.enum(['never', 'snippet']),
  /** Number of configured custom matchers (the regexes themselves stay in admin-config). */
  customMatcherCount: z.number().int().nonnegative(),
});
export type PublicPrivacyConfig = z.infer<typeof PublicPrivacyConfigSchema>;

// ============================================================================
// v2 — SOC2 / HIPAA evidence export (Ship 53 closure)
// ============================================================================
//
// Closes the v2 row from the design doc — auditor-ingestible PDF
// "evidence pack" composed off the same `WeeklyPrivacyReport`
// shape. Pure metadata is exposed on the wire; the PDF itself is
// streamed back as `application/pdf` and never round-tripped via
// JSON. Locked decision §9 still applies — the PDF body is
// composed only from already-redacted finding payloads.

/**
 * Optional metadata fields the operator can attach to an evidence
 * pack (preparer name, audit scope, custom note). Persisted in the
 * PDF cover page; never written to the DB.
 *
 * Caps are conservative so the PDF stays one printable A4 page for
 * the cover even when the operator fills every field.
 */
export const PrivacyEvidenceExportRequestSchema = z.object({
  /** ISO week to export — same shape as the weekly-report route. */
  week: z.string().regex(/^\d{4}-W\d{2}$/),
  /** Optional preparer name (free-form, shown on the cover page). */
  preparedBy: z.string().max(120).optional(),
  /** Optional audit scope / framework (e.g. "SOC 2 CC6.7" / "GDPR Art. 30"). */
  auditScope: z.string().max(120).optional(),
  /** Optional free-form note (one paragraph, shown beneath the summary). */
  note: z.string().max(500).optional(),
});
export type PrivacyEvidenceExportRequest = z.infer<typeof PrivacyEvidenceExportRequestSchema>;

/**
 * JSON response shape when the caller asks for `Accept: application/json`
 * (the SPA does this from the "Export evidence pack" button so it
 * can show a "ready — N bytes" toast + a download link). The actual
 * PDF bytes are NOT included; the SPA follows the `downloadUrl` on
 * a second request for the binary.
 *
 * The default response type for the route is `application/pdf` —
 * this metadata shape only fires when the client opts in via the
 * `Accept` header (mirrors the catalogue PDF route's pattern).
 */
export const PrivacyEvidenceExportResponseSchema = z.object({
  projectId: z.string().uuid(),
  week: z.string().regex(/^\d{4}-W\d{2}$/),
  windowStart: z.string().datetime(),
  windowEnd: z.string().datetime(),
  /** Bytes on disk — useful for the SPA to show "Generated 4.2 MB". */
  bytes: z.number().int().nonnegative(),
  /** True iff the PDF was served from the on-disk cache (no re-render). */
  fromCache: z.boolean(),
  /** Suggested filename — `privacy-evidence-<project>-<week>.pdf`. */
  filename: z.string().min(1).max(200),
  /** Total findings (post-dedup) included in the pack. */
  findingsCount: z.number().int().nonnegative(),
  /** Total third-party domains rolled up in the pack. */
  thirdPartyDomainCount: z.number().int().nonnegative(),
});
export type PrivacyEvidenceExportResponse = z.infer<typeof PrivacyEvidenceExportResponseSchema>;
